import flagsmith from "flagsmith";
import * as Flagsmith from "flagsmith/react";
import { FlagsmithProvider } from "flagsmith/react";
import { IInitConfig } from "flagsmith/types";
import { ReactElement, useEffect, useMemo } from "react";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import useUser from "src/hooks/useUser";
import * as Env from "src/services/env";
import { Status } from "src/types/authData";
import { FeatureFlag } from "src/types/featureFlag";
import * as RD from "src/types/remoteData";

type Props = {
  children: ReactElement;
};
export function FeatureFlagProvider({ children }: Props) {
  const env = Env.read();
  const user = useUser();
  const organization = useOrganizationPath();

  const userId =
    user.status === Status.OK
      ? user.data.id
      : user.status === Status.UNAUTHENTICATED
      ? "unauthenticated"
      : undefined;
  const role = user.status === Status.OK ? user.data.role : undefined;

  useEffect(() => {
    if (userId) {
      flagsmith.identify(userId, {
        ...(role ? { role } : {}),
        ...(organization ? { organization } : {}),
      });
    }
  }, [organization, userId, role]);

  const options: IInitConfig = useMemo(
    () => ({
      api: `${env.VITE_FLAGSMITH_URL}api/v1/`,
      environmentID: env.VITE_FLAGSMITH_ENVIRONMENT_KEY,
      cacheFlags: true,
      enableAnalytics: true,
    }),
    [env.VITE_FLAGSMITH_ENVIRONMENT_KEY, env.VITE_FLAGSMITH_URL]
  );

  return (
    <FlagsmithProvider options={options} flagsmith={flagsmith}>
      {children}
    </FlagsmithProvider>
  );
}

export type FeatureFlagReturnType = ReturnType<
  typeof Flagsmith.useFlags<FeatureFlag>
>;

// useFlag with type safety from type generated by `npm run feature-flag:codegen` script
export function useFlags(requiredFlags: FeatureFlag[]): FeatureFlagReturnType {
  return Flagsmith.useFlags<FeatureFlag>(requiredFlags);
}

export function useFlagsRemoteData(requiredFlags: FeatureFlag[]) {
  const flags = Flagsmith.useFlags<FeatureFlag>(requiredFlags);
  const loading = Flagsmith.useFlagsmithLoading();
  return RD.fromFlagsmith(loading, flags);
}
